import {
  useCallback,
  useLayoutEffect,
  useState,
  type TransitionEvent,
} from 'react';
import { motionMs, type MotionDuration } from './motion';

/**
 * Where a thing that comes and goes is in doing so.
 * - `unmounted`: not in the page.
 * - `closed`: in the page, still drawn as it is when closed. This is the one render the style change that follows is a
 *   transition from.
 * - `opening`: drawn as open, getting there.
 * - `open`: drawn as open, and there.
 * - `closing`: drawn as closed, getting there, and still in the page.
 */
export type ExitPhase = 'unmounted' | 'closed' | 'opening' | 'open' | 'closing';

export interface UseExitTransitionArgs {
  /** Whether it is wanted. Turning it off starts the exit; the thing leaves the page when that ends. */
  readonly open: boolean;
  /** Which of the standard's durations it runs for. Default the panel's. */
  readonly size?: MotionDuration;
  /** A duration in milliseconds in place of the standard's, for a test. Nil ends each step at once. */
  readonly durationMs?: number;
  /** Whether a thing that is open when first drawn animates in. Default no: it is simply there. */
  readonly animateOnMount?: boolean;
}

export interface UseExitTransitionResult {
  readonly phase: ExitPhase;
  /** In the page: from the moment it is wanted until its exit has ended. */
  readonly mounted: boolean;
  /** Drawn as open, and wanted. False in the render where it stops being wanted, so nothing shows open while it leaves. */
  readonly entered: boolean;
  /** Open and finished getting there: the time to take off what was clipping it while it moved. */
  readonly settled: boolean;
  /** For the animated element's `onTransitionEnd`: ends the current step when the element's own transition does. */
  readonly onTransitionEnd: (event: TransitionEvent<HTMLElement>) => void;
}

function startingPhase(open: boolean, animateOnMount: boolean): ExitPhase {
  if (!open) {
    return 'unmounted';
  }
  return animateOnMount ? 'closed' : 'open';
}

/**
 * Keeps a thing in the page while it animates in and out, which a caller that unmounts it the moment it is not wanted
 * cannot do. It steps `unmounted`, `closed`, `opening`, `open` on the way in and `closing`, `unmounted` on the way out. The
 * step from `closed` to `opening` is made after the page has computed the closed style, so the change is a transition and
 * not a first style. Each wait lasts the standard's duration, read from the style sheet's custom property (nil under reduced
 * motion), and ends early when the element reports its own `transitionend`; the timer is the fallback for an element that
 * never does, such as one whose transition did not run.
 */
export function useExitTransition({
  open,
  size = 'panel',
  durationMs,
  animateOnMount = false,
}: UseExitTransitionArgs): UseExitTransitionResult {
  const [phase, setPhase] = useState<ExitPhase>(() =>
    startingPhase(open, animateOnMount)
  );

  useLayoutEffect(() => {
    const waitThen = (next: ExitPhase): (() => void) | undefined => {
      const wait = durationMs ?? motionMs(size);
      if (wait <= 0) {
        setPhase(next);
        return undefined;
      }
      const timer = setTimeout(() => setPhase(next), wait);
      return () => clearTimeout(timer);
    };

    if (open) {
      switch (phase) {
        case 'unmounted':
          setPhase('closed');
          return undefined;
        case 'closed':
          // Reading a layout value makes the browser compute the closed style now, so the change that follows is a
          // transition from it. It does not wait for an animation frame, which a tab that is not painting never delivers.
          void document.body.offsetWidth;
          setPhase('opening');
          return undefined;
        case 'closing':
          setPhase('opening');
          return undefined;
        case 'opening':
          return waitThen('open');
        default:
          return undefined;
      }
    }

    if (phase === 'unmounted') {
      return undefined;
    }
    if (phase !== 'closing') {
      setPhase('closing');
      return undefined;
    }
    return waitThen('unmounted');
  }, [open, phase, size, durationMs]);

  const onTransitionEnd = useCallback((event: TransitionEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    setPhase((current) => {
      if (current === 'closing') {
        return 'unmounted';
      }
      return current === 'opening' ? 'open' : current;
    });
  }, []);

  return {
    phase,
    mounted: phase !== 'unmounted',
    entered: open && (phase === 'opening' || phase === 'open'),
    settled: open && phase === 'open',
    onTransitionEnd,
  };
}
